<script lang="ts">
    import { formatDateOnly, formatMoney, formatTimeOnly } from "@frontend/Pages/Formats";
    import { ApiResult, ApiResultOfWalletTransaction, User, PromotionStatus } from "@frontend/walletapi";
    import { service } from "@frontend/services";
    import { onDestroy, onMount } from "svelte";
    import { Timeout } from "@frontend/timeout";
    import { CashMode, CashModeString, settings } from "@frontend/settings";
    import { GetCachedToken } from "@frontend/user";
    import { translate, currentLanguage } from "../Translator";
    export let player: User;
    export let dismiss: () => void;

    export let type: "withdraw" | "promo";

    let result : ApiResultOfWalletTransaction | undefined;
    let amount: number = 0;
    let hasActivePromotions: boolean = false;
    let activePromotionsMessage: string = "";

    onMount(() => {
        if (type == "withdraw")
        {
            amount = player?.LastWalletTransaction?.CashableBalance?.InCents ?? 0;
        }
        
        // Verificar si el usuario ya tiene promociones activas
        checkActivePromotions();
    });

    function checkActivePromotions() {
        if (player?.UserPromotional && player.UserPromotional.length > 0) {
            const activePromos = player.UserPromotional.filter(promo => 
                promo.State === PromotionStatus.Active
            );
            
            if (activePromos.length > 0) {
                hasActivePromotions = true;
                const promoNames = activePromos.map(promo => 
                    promo.Campaigns?.PromotionName || 'Unknown Promotion'
                ).join(', ');
                activePromotionsMessage = `El usuario ya tiene promociones activas: ${promoNames}`;
            }
        }
    }


    let printing: boolean = false;
    let printingTimeout: Timeout = new Timeout(2000, () => printing = false);
    onDestroy(() => {
        printingTimeout.Dispose();
    });
    
    function appendNumber(number: number): void {
        amount = amount * 10 + number;  
    }

    function add(number: number): void {
        amount += number;  
    }
    
    function insertDoubleZero() {
        amount *= 100;
    }
    
    function deleteLastDigit() {
        amount = Math.floor(amount / 10);
    }

    function keydown(event: KeyboardEvent) {
        if (event.key === "Enter") {
            Withdraw();
        }
        else if (event.key == "Backspace")
        {
            deleteLastDigit();
        }
        else
        {
            let n = parseInt(event.key);
            if (!isNaN(n))
            {
                amount = amount * 10 + parseInt(event.key);
            }
        }
    }

    function wholeBalance()
    {
        amount = player?.LastWalletTransaction?.CashableBalance?.InCents ?? 0;
    }

    function clearAmount()
    {
        amount = 0;
    }
    
    async function Withdraw() {
        result = await service.walletapi.withdraw({
            Token: sessionStorage.token,
            Data: {
                UserId: player.Id,
                DepositCashMode: CashModeString.Noncashable,
                Amount: {
                    InCents: amount,
                }
            },
        });

        if (result.Success)
        {
            printing = true;
            printingTimeout.Start();
        }

        console.log(result);
    }
    
    async function Deposit() {
        console.log("Valor de amount antes de enviar:", amount);
        console.log("Tipo de amount:", typeof amount);
        
        const depositData = {
            Token: sessionStorage.token,
            Data: {
                UserId: player.Id,
                DepositCashMode: CashModeString.Noncashable,
                Amount: {
                    InCents: amount,
                }
            },
        };
        
        console.log("Enviando datos al backend:", JSON.stringify(depositData, null, 2));
        
        result = await service.walletapi.promotionalDeposit(depositData);

        if (result.Success)
        {
            printing = true;
            printingTimeout.Start();
        }

        console.log("Respuesta del backend:", result);
        console.log("NoncashableAmount en la respuesta:", result.Data?.NoncashableAmount);
    }

    async function PrintTransaction()
    {
        if (!result?.Success)
        {
            console.log("No transaction");
            return;
        }

        if (!result.Data)
        {
            console.log("No data");
            return;
        }

        let printResult: ApiResult = await service.walletapi.printTransaction({
            Token: GetCachedToken(),
            Data: {
                Id: result.Data.Id
            }
        });

        if (result.Success)
        {
            printing = true;
            printingTimeout.Start();
        }
    }
    
    async function Dismiss()
    {
        dismiss();
    }
</script>

<h2 style="text-align: center;">
    {#if type == "withdraw"}
        Withdraw
    {:else if type == "promo"}
        Promo
    {/if}
</h2>
   
{#if result?.Success && result.Data}
    <div class="ui positive message">
        <div class="header">
            {#if type == "withdraw"}
                Withdraw {formatMoney(result.Data.NoncashableAmount)} successful
            {:else if type == "promo"}
                Deposit {formatMoney(result.Data.NoncashableAmount)} successful
            {/if}
        </div>
    </div>

    <div class="ui segment" style="display: flex;">
        <table class="receit-table" style="flex-grow: 1;">
            <tr>
                <td class="receit-title">Trans. ID</td>
                <td class="receit-value">#{result.Data.Id}</td>
            </tr>
            <tr>
                <td class="receit-title">Player ID</td>
                <td class="receit-value">#{result.Data.UserId}</td>
            </tr>
            <tr>
                <td class="receit-title">Cashier ID</td>
            </tr>
            <tr>
                <td class="receit-title">Trans. date</td>
                <td class="receit-value">{formatDateOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">Trans. time</td>
                <td class="receit-value">{formatTimeOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">Trans. type</td>
                <td class="receit-value">{result.Data.Type}</td>
            </tr>
            <tr>
                <td class="receit-title">Trans. amount</td>
                <td class="receit-value">
                    {#if type == "withdraw"}
                        {formatMoney(result.Data.NoncashableAmount)}
                    {:else if type == "promo"}
                        {formatMoney(result.Data.NoncashableAmount)}
                    {/if}
                </td>
            </tr>
            <tr>
                <td class="receit-title">{$settings.NoncashableName} Balance</td>
                <td class="receit-value">{formatMoney(result.Data.NoncashableBalance)}</td>
            </tr>
            <tr>
                <td class="receit-title">{$settings.CashableName} Balance</td>
                <td class="receit-value">{formatMoney(result.Data.CashableBalance)}</td>
            </tr>
        </table>
    </div>

    <div style="display: flex; justify-content: center;">
        <button class="ui primary fluid button" on:click={Dismiss}>Dismiss</button>
        {#if printing}
            <button class="ui fluid disabled button"><i class="loading spinner icon"></i>Printing</button>
        {:else}
            <button class="ui fluid button" on:click={PrintTransaction}>Print</button>
        {/if}
    </div>


{:else}

    <table class="ui very basic very compact unstackable table">
        <h3> Add promotional credits</h3>
        <tr>
            <td>Player ID</td>
            <td style="text-align: right">#{player.Id}</td>
        </tr>
        <tr>
            <td>Player Name</td>
            <td style="text-align: right">{player.FirstName ?? ""} {player.LastName ?? ""}</td>
        </tr>
        <tr>
            <td>Player email</td>
            <td style="text-align: right">{player.Email ?? ""}</td>
        </tr>
        <!-- {#if $settings?.DepositCashMode == DepositCashMode.Noncashable} -->
        <tr>
            <td>{$settings.NoncashableName}</td>
            <td style="text-align: right">{formatMoney(player?.LastWalletTransaction?.NoncashableBalance)}</td>
        </tr>
        <!-- {/if} -->
        <tr>
            <td>{$settings.CashableName}</td>
            <td style="text-align: right">{formatMoney(player?.LastWalletTransaction?.CashableBalance)}</td>
        </tr>
    </table>
    
    <div class="ui form">
        {#if result && !result.Success}
            <div class="ui negative message">
                {result.ErrorMessage ?? "Unknown error during transaction"}
            </div>
        {/if}

        {#if hasActivePromotions}
            <div class="ui negative message">
                <div class="header">
                    <i class="exclamation triangle icon"></i>
                    {$translate('Active Promotions Found')}
                </div>
                <p>{activePromotionsMessage}</p>
                <p><strong>{$translate('Cannot add new promotional credits while active promotions exist.')}</strong></p>
            </div>
        {/if}

        <div class="field">
            <div class="ui labeled action input" style="text-align: right;">
                <div class="ui label">Amount</div>
                <input type="text" style="text-align: right" readonly value={formatMoney(amount)} on:keydown={keydown}>
                <button class="ui icon button" on:click={clearAmount}>
                    <i class="close icon"></i>
                </button>
            </div>
        </div>
        <div class="field">
            <div class="two fields">
                <div class="field">
                    <button class="ui negative fluid button" on:click={Dismiss}>Cancel</button>
                </div>
                <div class="field">
                    {#if type == "withdraw"}
                        <button class="ui positive fluid button" on:click={Withdraw}>Withdraw</button>
                    {:else if type == "promo"}
                        <button 
                            class="ui positive fluid button" 
                            class:disabled={hasActivePromotions}
                            on:click={Deposit}
                            disabled={hasActivePromotions}
                        >
                            {hasActivePromotions ? $translate('Promotions Active') : $translate('Deposit')}
                        </button>
                    {/if}
                </div>
            </div>
        </div>
    </div>

    <div class="common-values">
        <div class="row">
            <div>
                <button class="ui fluid button" on:click={() => add(100)}>$1</button>
            </div>
            <div>
                <button class="ui fluid button" on:click={() => add(200)}>$2</button>
            </div>
            <div>
                <button class="ui fluid button" on:click={() => add(500)}>$5</button>
            </div>
            <div>
                <button class="ui fluid button" on:click={() => add(1000)}>$10</button>
            </div>
        </div>

        <div class="row">
            <div>
                <button class="ui fluid button" on:click={() => add(2000)}>$20</button>
            </div>
            <div>
                <button class="ui fluid button" on:click={() => add(5000)}>$50</button>
            </div>
            <div>
                <button class="ui fluid button" on:click={() => add(10000)}>$100</button>
            </div>
        </div>
    </div>

    <table style="margin: auto">
        <thead>
            <tr>
                <td colspan="3">

                </td>
            </tr>
            <tr>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(1)}>1</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center; " on:click={() => appendNumber(2)}>2</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(3)}>3</button></td>
            </tr>
            <tr>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(4)}>4</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(5)}>5</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(6)}>6</button></td>
            </tr>
            <tr>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(7)}>7</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(8)}>8</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(9)}>9</button></td>
            </tr>
            <tr>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={() => appendNumber(0)}>0</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={insertDoubleZero}>00</button></td>
                <td><button class="ui button" style="width: 75px;  height: 35px; text-align: center;" on:click={deleteLastDigit} ><i class="arrow alternate circle left icon" /></button></td>
            </tr>
        </thead>
    </table>

{/if}

<style>
    .receit-table
    {
        border-spacing: 0px;
        font-family: 'Courier New', Courier, monospace;
    }

    .receit-table td
    {
        padding-top: 5px;
    }

    .receit-table td:first-child
    {
        margin-right: 0.2em;
    }

    .receit-title
    {
        text-align: left;
        border-bottom: 1px solid black;
        padding-right: 10px;
    }

    .receit-value
    {
        text-align: right;
        border-bottom: 1px solid black;
    }

    .common-values .row
    {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .common-values .row > div
    {
        flex-grow: 1;
        margin: 0.2rem;
    }
</style>
